import { useState, Fragment, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  Button,
  PersonAvatar,
  ProgressBar,
  Label,
  InputTextarea,
  InputText,
} from '@teo/components';
import {
  CrossFilled,
  ChevronRight,
  ChevronDown,
  Plus,
} from '@teo/components/icons';
import { format } from 'date-fns';
import i18n from 'i18next';
import { useGet } from '@/query/fetchHooks';
import { axios } from '@/axios';
import { config } from '@/config';
import { newUid } from '@/util';
import { useMediaQuery } from 'react-responsive';

const RigthSidebar = ({
  setIsOpenUser,
  activeFollower,
  setActiveUser,
  setActiveFollower,
  learningpath,
  setResetTable,
  user = false,
}) => {
  const { t } = useTranslation();
  const { organisationId } = useParams();
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const authUser = auth?.user;
  const [isOpenComments, setIsOpenComments] = useState(false);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [evaluators, setEvaluators] = useState(null);
  const [addedBy, setAddedBy] = useState(null);
  const teamPath = `/${i18n.language}/follow-up/${organisationId}/teams`;

  const [commentDetail, setCommentDetail] = useState([]);
  const [addComment, setAddComment] = useState(false);
  const [studentComment, setStudentComment] = useState('');

  const [edit, setEdit] = useState();
  const [editUserComment, setEditUserComment] = useState('');

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  useGet(`/lpteachdetails`, {
    lpgroupId: learningpath?.id,
    organisationId: organisationId,
    onSuccess: (result) => {
      setEvaluators(result?.data);
    },
  });

  useGet(`/memberdetails`, {
    enabled: user,
    organisationId: organisationId,
    userId: activeFollower?.userId,
    onSuccess: (result) => {
      setAddedBy(result?.data);
    },
  });

  const { refetch: refetchComments } = useGet(`/lpstudentcommentdetails`, {
    enabled: user,
    studentId: activeFollower?.studentId,
    lpgroupId: activeFollower?.lpgroupId,
    sort: '-createdAt',
    onSuccess: (result) => {
      setCommentDetail(result?.data || []);
    },
  });

  const handleScroll = () => {
    const scrollSection = document.getElementById('details-section');
    const footer = document.getElementById('footer_page');
    const scrollY = window.scrollY;
    if (scrollSection) {
      if (scrollY < 81) {
        scrollSection.style.marginTop = 80 - scrollY + 'px';

        const heightTop = 80 - scrollY;
        scrollSection.style.maxHeight = `calc(100vh - ${heightTop}px)`;
      } else {
        scrollSection.style.marginTop = '0px';
        scrollSection.style.maxHeight = `calc(100vh)`;
      }
    }
    if (footer) {
      const rect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const isVisible = rect.top < windowHeight && rect.bottom > 0;

      let visible = 0;
      if (isVisible) {
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, windowHeight);
        visible = visibleBottom - visibleTop;
        if (scrollY > 80) {
          scrollSection.style.maxHeight = `calc(100vh - ${visible - 1}px)`;
        } else {
          scrollSection.style.maxHeight = `calc(100vh - ${
            visible - 1 + 80 - scrollY
          }px)`;
        }
      }
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const possibleTints = [
    'bg-tint-aqua',
    'bg-tint-teal',
    'bg-tint-blue',
    'bg-tint-indigo',
    'bg-tint-purple',
    'bg-tint-pink',
    'bg-tint-red',
    'bg-tint-brown',
    'bg-tint-gold',
    'bg-tint-green',
  ];

  const hashCode = (string) => {
    const chars = string.split('');
    return chars.reduce((a, b) => (a + b.charCodeAt(0)) | 0, 0);
  };

  const colorIndex = (string, length) => {
    const code = hashCode(string);
    return Math.abs(Math.floor(code % length));
  };

  const getTint = (id, name) => {
    return possibleTints[colorIndex(`${id}-${name}`, possibleTints.length)];
  };

  const getInitialsFromName = (name) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  useEffect(() => {
    isMobile &&
      setTimeout(() => {
        document.body.style.overflow = 'hidden';
      }, 0);
  }, []);

  const handleAddComment = () => {
    axios
      .post(`${config.backend}/lpstudentcomments`, {
        authorId: authUser?.id,
        comment: studentComment,
        lpgroupId: learningpath?.lpgroupId,
        studentId: activeFollower?.studentId,
        authorName: authUser?.username,
      })
      .then(() => {
        setResetTable(newUid(10));
        refetchComments();
        setStudentComment('');
      });
  };

  return (
    <div
      id="details-section"
      className={`fixed flex flex-col overflow-y-auto border bg-white
        ${
          isMobile
            ? '!max-w-[calc(100vw)]] top-0 right-0 z-[1000] !mt-0 !h-full !max-h-[calc(100vh)] !w-full'
            : '-top-[1px] right-0 z-10 mt-[80px] h-full max-h-[calc(100vh_-_150px)] w-full max-w-[380px]'
        }
        `}
    >
      <div className="flex flex-col gap-4 border-b p-5">
        <div className="flex items-center">
          {user ? (
            <PersonAvatar
              inline
              id={activeFollower?.studentId}
              name={activeFollower.username || ''}
              imageSrc={activeFollower.picture || undefined}
            ></PersonAvatar>
          ) : (
            <PersonAvatar
              inline
              id={activeFollower.id}
              name={activeFollower.teamName}
              bgColor={activeFollower.color ? activeFollower.color : null}
            ></PersonAvatar>
          )}
          <div>
            <div
              className={`overflow-hidden text-ellipsis text-sm font-medium ${
                i18n.language === 'fr' ? 'max-w-[135px]' : 'max-w-[180px]'
              }`}
            >
              {user ? activeFollower?.username : activeFollower?.teamName}
            </div>
            {user && (
              <div
                className={`max-w-[180px] overflow-hidden text-ellipsis text-xs text-[#7d7d81] ${
                  i18n.language === 'fr' ? 'max-w-[135px]' : 'max-w-[180px]'
                }`}
              >
                {activeFollower?.email}
              </div>
            )}
          </div>
          <div className="ml-auto pl-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                document.body.style.overflow = 'unset';
                setActiveFollower(null);
                setActiveUser('');
                setIsOpenUser(false);
              }}
              className="!px-1"
            >
              <CrossFilled className="h-5 w-5 text-grey-05" />
            </Button>
          </div>
        </div>

        {user && (
          <div className="flex items-center">
            <div className="flex w-full">
              <ProgressBar
                max={100}
                value={30}
                className="h-1 overflow-hidden rounded-lg"
              />
            </div>
            <span className="pl-3 text-xs text-grey-06">
              {Math.round(activeFollower?.lpProgress * 100)}%
            </span>
          </div>
        )}
        <div className="flex items-center">
          {user ? (
            <Button size="sm" className="mr-2" variant="outline">
              {t('pages.follow_learningpath.view_progress')}
            </Button>
          ) : (
            <Button
              size="sm"
              className="mr-2"
              variant="outline"
              onClick={() => navigate(`${teamPath}/${activeFollower.id}`)}
            >
              {t('pages.follow_learningpath.view_team')}
            </Button>
          )}
          <Button size="sm" variant="warning">
            {t('pages.follow_learningpath.end_lp')}
          </Button>
        </div>
        {user && (
          <div className="flex">
            <div className="mr-8 flex flex-col">
              <p className="text-sm">{t('columns.started')}</p>
              <p className='"text-sm text-grey-06'>
                {activeFollower?.startDate
                  ? format(
                      new Date(activeFollower?.startDate || ''),
                      'dd/MM/yyyy'
                    )
                  : '-'}
              </p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm">{t('columns.last_activity')}</p>
              <p className="text-sm text-grey-06">
                {activeFollower?.updatedAt
                  ? format(
                      new Date(activeFollower?.updatedAt || ''),
                      'dd/MM/yyyy'
                    )
                  : '-'}
              </p>
            </div>
          </div>
        )}
      </div>
      {user && (
        <div className="flex flex-col border-b p-5">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              className="!px-1"
              onClick={() => {
                setIsOpenComments(!isOpenComments);
                setAddComment(false);
              }}
            >
              {isOpenComments ? (
                <ChevronDown className="h-5 w-5 text-grey-07" />
              ) : (
                <ChevronRight className="h-5 w-5 text-grey-07" />
              )}
            </Button>
            <p className="ml-2 font-medium">
              {t('pages.correction_result.comment')}
            </p>
            <div className="ml-auto">
              {commentDetail?.length > 0 && (
                <div className="text-xs-medium flex items-center justify-center rounded bg-tint-red py-0.5 px-1.5 text-white">
                  {commentDetail?.length}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 !px-1"
              onClick={() => {
                setIsOpenComments(true);
                setAddComment(true);
              }}
            >
              <Plus className="h-5 w-5 text-black" />
            </Button>
          </div>
          {isOpenComments && (
            <div className="flex flex-col">
              <div className="">
                {commentDetail?.map((el) => (
                  <div key={el.id}>
                    {el.comment && (
                      <div>
                        <div className="mt-3 mb-2 flex flex-wrap items-center">
                          <div
                            className={`${getTint(
                              el?.id,
                              el?.authorName
                            )} mr-3 inline-flex h-6 min-h-6 w-6 min-w-6 items-center justify-center overflow-hidden rounded-full text-xs font-semibold leading-4 text-white after:absolute after:h-6 after:w-6 after:rounded-full after:opacity-[.16] after:content-['']`}
                          >
                            <div className="flex h-6 min-h-6 w-6 min-w-6 items-center justify-center">
                              {el?.authorPicture ? (
                                <img
                                  className={`h-full object-cover`}
                                  src={el?.authorPicture}
                                  alt={el?.authorName}
                                />
                              ) : (
                                <span className="">
                                  {getInitialsFromName(
                                    el?.authorName ? el?.authorName : 'A'
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          <span className="text-sm font-normal text-grey-10">
                            {el?.authorName}
                          </span>
                        </div>

                        {edit === el.id ? (
                          <InputText
                            id="userComment"
                            onChange={async (event) => {
                              setEditUserComment(event.target.value);
                            }}
                            onBlur={async () => {
                              await axios.put(
                                `${config.backend}/lpstudentcomment/${learningpath.id}/${el.id}`,
                                {
                                  comment: editUserComment,
                                }
                              );
                            }}
                            onKeyPress={async () => {
                              await axios.put(
                                `${config.backend}/lpstudentcomment/${learningpath.id}/${el.id}`,
                                {
                                  comment: editUserComment,
                                }
                              );
                            }}
                            placeholder={
                              editUserComment ? editUserComment : el.comment
                            }
                            className="text-grey-07"
                          />
                        ) : (
                          <p className="mt-1 rounded bg-grey-01 py-2 px-3 text-sm font-normal text-grey-07">
                            {el.comment}
                          </p>
                        )}
                        <div className="mt-2 flex flex-col">
                          <span className="text-xs text-grey-06">
                            {format(
                              new Date(el.createdAt || ''),
                              'dd/MM/yyyy HH:mm'
                            )}
                          </span>
                        </div>
                        {el?.authorId === authUser?.id && (
                          <div>
                            <div className="mt-1 flex flex-row items-center">
                              <Button
                                variant="text"
                                size="sm"
                                className="text-tint-blue"
                                onClick={() => setEdit(el?.id)}
                              >
                                {t('pages.follow_up_teams.to_process')}
                              </Button>
                              <span className="px-1">&#8729;</span>
                              <Button
                                variant="text"
                                size="sm"
                                className="text-tint-blue"
                                onClick={() =>
                                  axios
                                    .delete(
                                      `${config.backend}/lpstudentcomment/${learningpath.id}/${el.id}`
                                    )
                                    .then(() => {
                                      setResetTable(newUid(10));
                                      refetchComments();
                                    })
                                }
                              >
                                {t('pages.follow_up_teams.to_delete')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {addComment && (
                <div className="flex w-full flex-col">
                  <div className="mb-4">
                    <Label htmlFor="comment">
                      <p className="mt-6 text-sm font-semibold">
                        {t('pages.follow_up_teams.add_comment')}
                      </p>
                    </Label>
                    <InputTextarea
                      id="comment"
                      placeholder={t(
                        'pages.follow_up_teams.write_your_comment'
                      )}
                      onChange={(e) => setStudentComment(e.target.value)}
                      limit={300}
                      value={studentComment}
                    />
                  </div>
                  <div className="flex justify-start">
                    <Button onClick={() => handleAddComment()}>
                      {t('pages.follow_up_teams.add_comment')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col border-b p-5">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            className="!px-1"
            onClick={() => {
              setIsOpenDetails(!isOpenDetails);
            }}
          >
            {isOpenDetails ? (
              <ChevronDown className="h-5 w-5 text-grey-07" />
            ) : (
              <ChevronRight className="h-5 w-5 text-grey-07" />
            )}
          </Button>
          <p className="ml-2 font-medium">
            {user
              ? t('pages.follow_learningpath.personal_lp_details')
              : t('pages.follow_learningpath.teams_lp_details')}
          </p>
        </div>
        {isOpenDetails && (
          <div className="mt-5 flex flex-col gap-4">
            {evaluators && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">
                  {t('pages.course_content_flow.evaluators')}
                </p>
                {evaluators?.map((el) => {
                  return (
                    <p key={el?.userId} className="text-sm text-tint-blue">
                      {el?.username}
                    </p>
                  );
                })}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.follow_learningpath.added_on')}
              </p>
              <p className="text-sm text-grey-06">
                {activeFollower?.createdAt
                  ? format(
                      new Date(activeFollower?.createdAt || ''),
                      'dd/MM/yyyy'
                    )
                  : '-'}
              </p>
            </div>
            {addedBy && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">
                  {t('pages.follow_learningpath.added_by')}
                </p>
                {addedBy?.map((el) => {
                  return (
                    <p key={el?.userId} className="text-sm text-tint-blue">
                      {el?.username}
                    </p>
                  );
                })}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{t('columns.version')}</p>
              <p className="text-sm text-grey-06">{learningpath?.version}</p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.create_courses.start_date')}
              </p>
              <p className="text-sm text-grey-06">
                {learningpath?.lpStartDate
                  ? format(
                      new Date(learningpath?.lpStartDate || ''),
                      'dd/MM/yyyy'
                    )
                  : '-'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.create_courses.end_date')}
              </p>
              <p className="text-sm text-grey-06">
                {learningpath?.lpEndDate
                  ? format(
                      new Date(learningpath?.lpEndDate || ''),
                      'dd/MM/yyyy'
                    )
                  : '-'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.create_courses.certificate')}
              </p>
              <p className="text-sm text-grey-06">
                {learningpath?.emailCertificate
                  ? t('delete_modal.yes')
                  : t('delete_modal.no')}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.learning_path.mandatory')}
              </p>
              <p className="text-sm text-grey-06">
                {learningpath?.isMandatory
                  ? t('delete_modal.yes')
                  : t('delete_modal.no')}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {t('pages.follow_up_teams.access')}
              </p>
              <p className="text-sm text-grey-06">
                {learningpath?.lpgroupAccess === 'protected' ? (
                  <>
                    {t('pages.course_content_flow.step_3.public')}&nbsp;
                    <span className="block font-semibold">
                      {authUser?.organisationName && authUser?.organisationName}
                    </span>
                  </>
                ) : (
                  t('pages.course_content_flow.step_3.ivite')
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RigthSidebar;
